import { CustomJobPhase } from "hooks/jobs/job-phases/useGetJobPhase";
import { CustomJobSchedules } from "hooks/schedules/useGetJobSchedules";
import generateBlocksForJobPhase from "utils/jobs/generateBlocksForJobPhase";
import { z } from "zod";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import localizedFormat from "dayjs/plugin/localizedFormat";

dayjs.extend(utc);
dayjs.extend(localizedFormat);

const blocksSchema = z.object({
  scheduledAt: z.string().min(1, "Scheduled at is required"),
  blockLength: z.number().positive(),
  showTravelTo: z.boolean(),
  showTravelFrom: z.boolean(),
});

const requiredSelectValue = z
  .string({
    required_error: "Please select a value",
    invalid_type_error: "Please select a value",
  })
  .min(1, "Please select a value");

export const schema = z
  .object({
    jobPhaseId: z.string().min(1, "Job Phase ID is required"),
    crewId: z.string().optional(),
    userIDs: z.array(z.string()).optional(),
    assignTo: requiredSelectValue,
    usersColor: z.string().optional(),
    travelTimeEstimate: z.null().or(z.number().optional()),
    blocks: z.array(blocksSchema),
  })
  .superRefine((values, ctx) => {
    if (values.assignTo === "crew" && !values.crewId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please select a value",
        path: ["crewId"],
      });
    }

    if (values.assignTo === "individual" && (!values.userIDs || values.userIDs.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please select a value",
        path: ["userIDs"],
      });
    }
  });

export type ScheduleJobPhaseInput = z.infer<typeof schema>;

export function getDefaultValues({
  jobPhase,
  jobSchedules,
  organizationSettings,
  showTravelTime,
  initialScheduledAt,
  initialDuration,
}: {
  jobPhase: CustomJobPhase;
  jobSchedules: CustomJobSchedules;
  organizationSettings: Record<string, string>;
  showTravelTime: boolean;
  initialScheduledAt?: Date | null;
  initialDuration?: number | null;
}) {
  const proposalCrewId =
    jobPhase?.proposalProduct?.crews?.length === 1 ? jobPhase.proposalProduct.crews[0].crewId : "";
  const userIDs = jobPhase?.users?.map((x) => x.id) ?? [];
  const hasScheduledIndividuals = userIDs.length > 0;
  const crewId = hasScheduledIndividuals ? "" : jobPhase?.crew?.id ?? proposalCrewId ?? "";
  const assignTo = hasScheduledIndividuals ? "individual" : crewId ? "crew" : "";

  // if there are no jobSchedules we need to return the blocks that
  // are limited to the daily block without a start date or end date

  // If we have an initial scheduled time from a drag-drop, use that for the first block
  const getBlocks = () => {
    if (jobSchedules.length > 0) {
      return jobSchedules.map((jobSchedule) => ({
        scheduledAt: dayjs.utc(jobSchedule.scheduledAt).local().format("YYYY-MM-DD HH:mm:ss"),
        blockLength: jobSchedule.blockLength,
        showTravelTo: jobSchedule.showTravelTo,
        showTravelFrom: jobSchedule.showTravelFrom,
      }));
    }

    const blocks = generateBlocksForJobPhase({
      jobPhase,
      organizationSettings,
      showTravelTo: showTravelTime,
      showTravelFrom: showTravelTime,
    });

    if (initialScheduledAt && blocks.length > 0) {
      return blocks.map((block, index) => ({
        ...block,
        scheduledAt: dayjs(initialScheduledAt).add(index, "day").format("YYYY-MM-DD HH:mm:ss"),
      }));
    }

    return blocks;
  };

  return {
    jobPhaseId: jobPhase.id,
    crewId,
    userIDs,
    assignTo,
    travelTimeEstimate: jobPhase.travelTimeEstimate,
    blocks: getBlocks(),
  };
}
